import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTrialContext } from '../context/TrialContext';
import { useSubscription } from '../context/SubscriptionContext';
import UpgradeModal from '../components/UpgradeModal';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SolveQuestion = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { trialEmail, isTrialActive, daysRemaining, quizzesToday, quizzesLimit } = useTrialContext();
  const { isPremium, isFree } = useSubscription();

  // Input mode: 'text', 'image', 'pdf'
  const [inputMode, setInputMode] = useState('text');
  const [question, setQuestion] = useState('');
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [solution, setSolution] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Check if user can access solver (Premium or Trial users only)
  const canAccessSolver = isPremium || isTrialActive;

  // If free tier user (logged in but no subscription and not on trial), show locked page
  if (user && isFree && !isTrialActive) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <UpgradeModal 
          isOpen={true} 
          onClose={() => navigate('/dashboard')}
          feature="solver"
        />
        
        <nav className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm border-b`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16 items-center">
              <button onClick={() => navigate('/dashboard')} className={`flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Dashboard
              </button>
            </div>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto py-16 px-4 text-center">
          <div className="text-6xl mb-6">🔒</div>
          <h1 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            AI Question Solver
          </h1>
          <p className={`text-lg mb-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Get step-by-step solutions to any question. Upload images, PDFs, or type your questions.
          </p>
          <button
            onClick={() => navigate('/pricing')}
            className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-bold text-lg hover:from-indigo-700 hover:to-purple-700"
          >
            Upgrade to Premium
          </button>
        </div>
      </div>
    );
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
    }
  };

  const buildHeaders = () => {
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else if (trialEmail) {
      headers['X-Trial-Email'] = trialEmail;
    }
    return headers;
  };

  const handleSolve = async () => {
    if (inputMode === 'text' && (!question || question.trim().length < 5)) {
      setError('Please enter a valid question (at least 5 characters)');
      return;
    }
    if ((inputMode === 'image' || inputMode === 'pdf') && !file) {
      setError(`Please upload ${inputMode === 'image' ? 'an image' : 'a PDF'} file`);
      return;
    }

    setLoading(true);
    setError('');
    setSolution(null);

    try {
      let response;

      if (inputMode === 'text') {
        response = await axios.post(
          `${API_URL}/api/quiz/solve`,
          { question },
          { headers: buildHeaders() }
        );
      } else if (inputMode === 'image') {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('additional_context', question || '');
        
        response = await axios.post(
          `${API_URL}/api/quiz/solve-from-image`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data', ...buildHeaders() } }
        );
      } else if (inputMode === 'pdf') {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('additional_context', question || '');
        
        response = await axios.post(
          `${API_URL}/api/quiz/solve-from-pdf`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data', ...buildHeaders() } }
        );
      }

      setSolution(response.data);
    } catch (err) {
      console.error('Error solving question:', err);
      setError(err.response?.data?.detail || 'Failed to solve question. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setQuestion('');
    setFile(null);
    setFileName('');
    setSolution(null);
    setError('');
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} transition-colors duration-300`}>
      {/* Navigation */}
      <nav className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm border-b`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <button
                onClick={() => navigate(user ? '/dashboard' : '/')}
                className={`flex items-center ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back
              </button>
            </div>
            
            <Link to="/" className="flex items-center">
              <div className="w-8 h-8 rounded-lg overflow-hidden bg-[#b8e1f5]">
                <img src="/logo.png" alt="Study Quiz" className="w-8 h-8 object-cover" />
              </div>
              <span className="ml-2 text-lg font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Study Quiz</span>
            </Link>

            <div className="flex items-center space-x-3">
              {/* Trial Status Badges */}
              {isTrialActive && !user && (
                <>
                  <span className="px-3 py-1 bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 text-xs font-medium rounded-full">
                    Trial: {daysRemaining} days left
                  </span>
                  <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-full">
                    {quizzesLimit - quizzesToday} solves left today
                  </span>
                </>
              )}
              
              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                className={`p-2 rounded-lg ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
              >
                {isDarkMode ? (
                  <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className={`text-4xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            AI Question Solver
          </h1>
          <p className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Get detailed solutions and step-by-step explanations for any question
          </p>
        </div>

        {!solution ? (
          <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm p-8 border`}>
            {/* Input Mode Selector */}
            <div className="mb-8">
              <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Input Method
              </label>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { id: 'text', icon: '📝', label: 'Type Question' },
                  { id: 'image', icon: '📷', label: 'Upload Image' },
                  { id: 'pdf', icon: '📄', label: 'Upload PDF' }
                ].map((mode) => (
                  <button
                    key={mode.id}
                    onClick={() => { setInputMode(mode.id); setFile(null); setFileName(''); }}
                    className={`p-4 border-2 rounded-lg text-center transition-all ${
                      inputMode === mode.id
                        ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                        : isDarkMode 
                          ? 'border-gray-600 hover:border-gray-500 text-gray-300' 
                          : 'border-gray-300 hover:border-gray-400 text-gray-700'
                    }`}
                  >
                    <div className="text-3xl mb-2">{mode.icon}</div>
                    <div className="font-medium text-sm">{mode.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Text Input */}
            {inputMode === 'text' && (
              <div className="mb-6">
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Enter your question
                </label>
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  rows={6}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                  placeholder="Example: Solve the quadratic equation 2x² + 5x - 3 = 0

Or paste any homework question, math problem, science question, etc..."
                />
              </div>
            )}

            {/* Image Upload */}
            {inputMode === 'image' && (
              <div className="mb-6">
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Upload image of your question
                </label>
                <div className={`border-2 border-dashed rounded-lg p-8 text-center hover:border-amber-400 transition-colors ${
                  isDarkMode ? 'border-gray-600' : 'border-gray-300'
                }`}>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="hidden"
                    id="image-upload"
                  />
                  <label htmlFor="image-upload" className="cursor-pointer flex flex-col items-center">
                    <div className="text-5xl mb-4">📷</div>
                    {fileName ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-center space-x-2 text-green-600">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="font-medium">{fileName}</span>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); setFile(null); setFileName(''); }}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Remove file
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                          Click to upload image
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          JPG or PNG (max 5MB)
                        </p>
                      </div>
                    )}
                  </label>
                </div>
                <div className="mt-4">
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Additional context (optional)
                  </label>
                  <input
                    type="text"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                    placeholder="e.g., 'This is from Chapter 5' or 'Need step-by-step solution'"
                  />
                </div>
              </div>
            )}

            {/* PDF Upload */}
            {inputMode === 'pdf' && (
              <div className="mb-6">
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Upload PDF with your question
                </label>
                <div className={`border-2 border-dashed rounded-lg p-8 text-center hover:border-amber-400 transition-colors ${
                  isDarkMode ? 'border-gray-600' : 'border-gray-300'
                }`}>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="hidden"
                    id="pdf-upload"
                  />
                  <label htmlFor="pdf-upload" className="cursor-pointer flex flex-col items-center">
                    <div className="text-5xl mb-4">📄</div>
                    {fileName ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-center space-x-2 text-green-600">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="font-medium">{fileName}</span>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); setFile(null); setFileName(''); }}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Remove file
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                          Click to upload PDF
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          PDF file (max 10MB)
                        </p>
                      </div>
                    )}
                  </label>
                </div>
                <div className="mt-4">
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Additional context (optional)
                  </label>
                  <input
                    type="text"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                    placeholder="e.g., 'Focus on question 3' or 'Explain the theorem'"
                  />
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
                <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
              </div>
            )}

            {/* Solve Button */}
            <button
              onClick={handleSolve}
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5 mr-3 text-white" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Analyzing & Solving...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Solve Question
                </>
              )}
            </button>
          </div>
        ) : (
          /* Solution Display */
          <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border overflow-hidden`}>
            {/* Solution Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white">Solution</h2>
                  <p className="text-amber-100 mt-1">Subject: {solution.subject || 'General'}</p>
                </div>
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Question */}
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <h3 className={`font-bold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Question</h3>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{solution.question}</p>
              </div>

              {/* Answer */}
              <div className={`p-4 rounded-lg border-2 border-green-200 dark:border-green-700 ${isDarkMode ? 'bg-green-900/20' : 'bg-green-50'}`}>
                <h3 className="font-bold mb-2 text-green-700 dark:text-green-400">Answer</h3>
                <p className={`${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{solution.solution}</p>
              </div>

              {/* Steps */}
              {solution.steps && solution.steps.length > 0 && (
                <div>
                  <h3 className={`font-bold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Step-by-Step Solution</h3>
                  <div className="space-y-3">
                    {solution.steps.map((step, index) => (
                      <div key={index} className={`flex items-start p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold text-sm mr-3 flex-shrink-0">
                          {index + 1}
                        </div>
                        <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} pt-1`}>{step}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tips */}
              {solution.tips && solution.tips.length > 0 && (
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'} border`}>
                  <h3 className="font-bold mb-3 text-blue-700 dark:text-blue-400">Helpful Tips</h3>
                  <ul className="space-y-2">
                    {solution.tips.map((tip, index) => (
                      <li key={index} className={`flex items-start ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <span className="text-blue-500 mr-2">💡</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <button
                  onClick={resetForm}
                  className="flex-1 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all"
                >
                  Solve Another Question
                </button>
                <button
                  onClick={() => navigate(user ? '/dashboard' : '/')}
                  className={`px-6 py-3 border-2 rounded-xl font-bold transition-all ${
                    isDarkMode 
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Back
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className={`mt-8 p-6 rounded-xl ${isDarkMode ? 'bg-amber-900/20 border-amber-700' : 'bg-amber-50 border-amber-200'} border`}>
          <div className="flex items-start">
            <div className="text-2xl mr-3">💡</div>
            <div>
              <h3 className={`font-bold ${isDarkMode ? 'text-amber-300' : 'text-amber-800'}`}>Pro Tips</h3>
              <ul className={`mt-2 space-y-1 text-sm ${isDarkMode ? 'text-amber-200' : 'text-amber-700'}`}>
                <li>• Take clear photos of your homework for best results</li>
                <li>• Works with math, science, history, language arts, and more</li>
                <li>• Add context to get more relevant explanations</li>
                <li>• Use text input for typed questions or equations</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SolveQuestion;
